/**
 * LEAD-ASSIGNMENT-OWNERSHIP-V1 (V2-P0) -- the Lead 360 owner CONTROLS + row.
 *
 * The FOURTH plane on the canonical Lead 360 detail: WHO is accountable for
 * this lead right now (a PERSON), DISTINCT from `openActionOwner` (the
 * next-action owner ROLE) and `assignedRole` (the responsible-role default).
 * This module renders the "Owner" highlight row (owner label, or
 * "Unassigned (pool)", plus an escalation hint) and the claim / assign /
 * release controls.
 *
 * BYTE-IDENTICAL FLAG-OFF: the owner ROW is gated on `record.ownerAssignment`
 * presence (absent => the assignment flag was off for this record => render
 * nothing new). The interactive CONTROLS are gated on `me.leadAssignmentEnabled
 * === true` AND the handlers being threaded in -- in tests / SSR (no handlers,
 * no `me`) the row still renders but the controls are omitted, so the pure
 * detail body stays testable.
 *
 * TESTABILITY (repo idiom -- react-dom/server, no jsdom): the affordance
 * decision (`resolveClaimAffordance`), the copy, and the pure
 * `LeadOwnerControlsView` are exports rendered from props; the default
 * container wires local submitting/error state around the injected async fns +
 * the assignable-staff loader. Role/flag gating is UX only -- the backend route
 * auth (503 + role gate + scope/IDOR/CAS) is the security boundary.
 *
 * PHI-free: renders ONLY the controlled owner label (staff, never a patient
 * value) + status tokens. No phone / name / clinical field ever reaches here.
 */

import { useEffect, useState } from 'react';

import { PrimaryAction, SecondaryAction } from '@/dashboard/ui';
import { SectionIcon } from '@/dashboard/owner/Lead360Panel';
import type { LeadOwnershipView } from '@/dashboard/owner/lead-360-view';
import type { AssignableStaffOption } from '@/dashboard/api';
import type { DashboardMe } from '@/dashboard/types';

// --- Copy (single source; asserted in tests) -------------------------

export const OWNER_ROW_LABEL = 'Owner';
/** STITCH-2 SCOPE 8(d): the designed unclaimed state -- plain language, one
 *  source (the quick view + highlights + this row all render THIS). */
export const OWNER_POOL_VALUE = 'Not yet claimed - in the shared queue';
export const OWNER_ESCALATED_HINT = 'Unclaimed past the escalation threshold.';
export const CLAIM_BUTTON_COPY = 'Claim';
export const RELEASE_BUTTON_COPY = 'Release to pool';
export const ASSIGN_LABEL_COPY = 'Assign';
export const REASSIGN_LABEL_COPY = 'Reassign';
export const ASSIGN_PLACEHOLDER_COPY = 'Select staff...';
/** Names the fix -- manager_assign mode disables operator self-claim. */
export const CLAIM_MANAGER_ASSIGN_COPY = 'A manager assigns leads here.';
/** Names the fix -- the caller has no staff profile yet. */
export const CLAIM_UNSEEDED_COPY = 'Set up your staff profile to claim.';
/** Names the fix -- the org has no assignable staff. */
export const ASSIGN_NO_STAFF_COPY = 'No staff to assign yet.';
export const CLAIM_SUCCESS_COPY = 'Lead claimed.';
export const ASSIGN_SUCCESS_COPY = 'Lead assigned.';
export const RELEASE_SUCCESS_COPY = 'Lead released to the pool.';

// --- Pure affordance decision ----------------------------------------

/**
 * The claim affordance for a POOL lead, computed from `me` + ownerAssignment.
 * A DISABLED variant always names the fix. Pure.
 *   - 'active'   : role may claim, mode permits, staff seeded -> live button.
 *   - 'manager'  : role may claim but mode is manager_assign -> disabled + copy.
 *   - 'unseeded' : role may claim but no staff profile -> disabled + copy.
 *   - 'none'     : role cannot claim -> no button.
 */
export type ClaimAffordance = 'active' | 'manager' | 'unseeded' | 'none';

export function resolveClaimAffordance(me: DashboardMe | null): ClaimAffordance {
  if (me === null || me.leadAssignmentEnabled !== true) return 'none';
  if (me.leadAssignmentClaimEnabled === true) return 'active';
  if (me.leadAssignmentRoleCanClaim === true) {
    if (me.leadAssignmentMode === 'manager_assign') return 'manager';
    if (me.staffIdentitySeeded === false) return 'unseeded';
  }
  return 'none';
}

/** Disabled-claim copy for the non-active affordances (null for active/none). */
export function claimDisabledCopy(affordance: ClaimAffordance): string | null {
  if (affordance === 'manager') return CLAIM_MANAGER_ASSIGN_COPY;
  if (affordance === 'unseeded') return CLAIM_UNSEEDED_COPY;
  return null;
}

/** The caller's role may assign / reassign to others (leadership tiers). */
export function canAssign(me: DashboardMe | null): boolean {
  return me !== null && me.leadAssignmentEnabled === true &&
    me.leadAssignmentRoleCanAssign === true;
}

/**
 * Release is offered when the caller OWNS the lead (isMine) OR is a leadership
 * assign tier (may release any lead to the pool). A pool lead (not assigned) is
 * never releasable.
 */
export function canRelease(
  me: DashboardMe | null,
  ownership: LeadOwnershipView,
): boolean {
  if (!ownership.assigned) return false;
  if (me === null || me.leadAssignmentEnabled !== true) return false;
  return ownership.isMine || me.leadAssignmentRoleCanAssign === true;
}

// --- Message + error mapping -----------------------------------------

export interface OwnerControlMessage {
  readonly tone: 'error' | 'success';
  readonly text: string;
}

export type OwnerActionKind = 'claim' | 'assign' | 'release';

function statusOf(err: unknown): number | null {
  if (err !== null && typeof err === 'object' && 'status' in err) {
    const s = (err as { status: unknown }).status;
    return typeof s === 'number' ? s : null;
  }
  return null;
}

function codeOf(err: unknown): string | null {
  if (err !== null && typeof err === 'object' && 'code' in err) {
    const c = (err as { code: unknown }).code;
    return typeof c === 'string' ? c : null;
  }
  return null;
}

/**
 * Map a backend failure (HTTP status + stable code, both from PortalApiError)
 * to a safe, friendly operator message. Pure; never leaks a raw DB error. The
 * 409 codes mirror api/routes/lead-assignment.ts (lost-update CAS + the domain
 * denials that surface as ConflictError).
 */
export function ownerActionErrorMessage(
  kind: OwnerActionKind,
  status: number | null,
  code: string | null,
): string {
  if (status === 503) return 'Lead assignment is not enabled.';
  if (status === 401 || status === 403) {
    return 'You do not have permission for this action.';
  }
  if (status === 409) {
    if (code === 'lead_already_assigned') {
      return 'This lead already has an owner; reload to see who.';
    }
    if (code === 'lead_not_assigned') return 'This lead is already in the pool.';
    if (code === 'lead_claim_disabled_in_mode') return CLAIM_MANAGER_ASSIGN_COPY;
    if (code === 'assignment_actor_unprovisioned') return CLAIM_UNSEEDED_COPY;
    return 'This lead ownership just changed; reload.';
  }
  if (status === 400) return 'Please choose a staff member to assign.';
  return kind === 'claim'
    ? 'Could not claim this lead. Please try again.'
    : kind === 'assign'
      ? 'Could not assign this lead. Please try again.'
      : 'Could not release this lead. Please try again.';
}

// --- Pure presentational view ----------------------------------------

export interface LeadOwnerControlsViewProps {
  ownership: LeadOwnershipView;
  /**
   * LEAD-RECORD-POLISH-BATCH-V1 (DENSITY rule 1 -- SAY IT ONCE): whether to
   * render the owner ROW. A host that already states the owner in its own
   * first-screen summary passes FALSE and gets the CONTROLS alone, so the
   * founder-found duplicate ("Not yet claimed" in the header AND again in this
   * card) is gone. DEFAULT TRUE => byte-identical for every other host (the
   * quick-view drawer keeps the row).
   */
  showOwnerRow?: boolean;
  /**
   * LEAD-360-CONFORMANCE-B2: FALSE skips this component's own
   * `<section><h4>Owner</h4>` chrome, rendering just the row + controls as
   * a fragment. DEFAULT TRUE => byte-identical for every existing host
   * (the quick-view drawer, the work-zone card). The composed rail's Owner
   * `SectionContainer` already draws the counted "Owner" header, so its
   * caller passes FALSE to avoid a doubled heading.
   */
  renderChrome?: boolean;
  /** The signed-in caller's /me payload (null in SSR / tests => row only). */
  me: DashboardMe | null;
  /** Leadership assignable-staff list (loaded lazily); empty until fetched. */
  staff: readonly AssignableStaffOption[];
  staffLoading: boolean;
  submitting: boolean;
  message: OwnerControlMessage | null;
  onClaim?: () => void;
  onRelease?: () => void;
  onAssignSelect?: (staffUserId: number) => void;
}

/**
 * The owner ROW (highlight) + controls. The row ALWAYS renders (the caller only
 * mounts this when `ownerAssignment` is present); the controls render only when
 * `me` + handlers are threaded in (live page). Reuses the shared status-chip /
 * badge / ac-btn tokens -- no new design doctrine.
 */
export function LeadOwnerControlsView({
  ownership,
  showOwnerRow = true,
  renderChrome = true,
  me,
  staff,
  staffLoading,
  submitting,
  message,
  onClaim,
  onRelease,
  onAssignSelect,
}: LeadOwnerControlsViewProps): JSX.Element {
  const interactive = me !== null && me.leadAssignmentEnabled === true;
  const claimAffordance = resolveClaimAffordance(me);
  const disabledClaimCopy = claimDisabledCopy(claimAffordance);
  const showRelease = canRelease(me, ownership);
  const showAssign = canAssign(me);

  const body = (
    <>
      {showOwnerRow ? (
      <dl className="lead360-kv" data-testid="lead-owner-row">
        <div className="lead360-kv__row">
          <dt className="lead360-kv__key">{OWNER_ROW_LABEL}</dt>
          <dd className="lead360-kv__val" data-testid="lead-owner-value">
            {ownership.assigned ? (
              /* Identity never borrows status colors (02 sec 9.5; 03 P2):
                 the owner is a PERSON chip on the brand pair, not a green
                 status. */
              <span
                className="ac-badge ac-badge--brand"
                data-testid="lead-owner-chip"
              >
                {ownership.ownerLabel ?? 'Assigned'}
              </span>
            ) : (
              /* The dashed EMPTY SEAT (03 P6) with the designed copy. */
              <span
                className="ac-badge ac-badge--pool"
                data-testid="lead-owner-pool"
              >
                {OWNER_POOL_VALUE}
              </span>
            )}
            {!ownership.assigned && ownership.escalated ? (
              <span
                className="badge badge--warning lead360-owner__escalated"
                data-testid="lead-owner-escalated"
              >
                {OWNER_ESCALATED_HINT}
              </span>
            ) : null}
          </dd>
        </div>
      </dl>
      ) : null}

      {interactive ? (
        <div className="lead360-owner__controls" data-testid="lead-owner-controls">
          {/* Claim -- only for a POOL lead. */}
          {!ownership.assigned && claimAffordance === 'active' ? (
            <PrimaryAction
              size="sm"
              data-testid="lead-owner-claim"
              disabled={submitting}
              onClick={onClaim}
            >
              {CLAIM_BUTTON_COPY}
            </PrimaryAction>
          ) : null}
          {!ownership.assigned && disabledClaimCopy !== null ? (
            // The explanation sits BESIDE the disabled control, never inside
            // its label (the sentence-in-a-pill defect, audit S4) -- same
            // copy, same testids, honest disabled state.
            <span className="lead360-owner__claimwrap">
              <SecondaryAction
                size="sm"
                data-testid="lead-owner-claim-disabled"
                disabled
                title={disabledClaimCopy}
              >
                {CLAIM_BUTTON_COPY}
              </SecondaryAction>
              <span className="lead360-muted lead360-owner__hint">
                {disabledClaimCopy}
              </span>
            </span>
          ) : null}

          {/* Assign / Reassign -- leadership only. TODO-SEAM (S3): the shared
              EntityLookup lands in V2-P2; until then this is a bespoke-minimal
              <select> over GET /api/leads/assignable-staff. */}
          {showAssign ? (
            <label
              className="lead360-owner__assign"
              data-testid="lead-owner-assign"
            >
              {ownership.assigned ? REASSIGN_LABEL_COPY : ASSIGN_LABEL_COPY}
              {': '}
              {staff.length === 0 && !staffLoading ? (
                <select
                  data-testid="lead-owner-assign-select"
                  disabled
                  value=""
                >
                  <option value="">{ASSIGN_NO_STAFF_COPY}</option>
                </select>
              ) : (
                <select
                  data-testid="lead-owner-assign-select"
                  disabled={submitting || staffLoading}
                  value=""
                  onChange={(e) => {
                    const id = Number(e.target.value);
                    if (Number.isInteger(id) && id > 0) onAssignSelect?.(id);
                  }}
                >
                  <option value="" disabled>
                    {staffLoading ? 'Loading...' : ASSIGN_PLACEHOLDER_COPY}
                  </option>
                  {staff.map((s) => (
                    <option key={s.staffUserId} value={s.staffUserId}>
                      {s.label}
                    </option>
                  ))}
                </select>
              )}
            </label>
          ) : null}

          {/* Release -- owner self-release or leadership. */}
          {showRelease ? (
            <SecondaryAction
              size="sm"
              data-testid="lead-owner-release"
              disabled={submitting}
              onClick={onRelease}
            >
              {RELEASE_BUTTON_COPY}
            </SecondaryAction>
          ) : null}

          {message !== null ? (
            <p
              className="lead360-muted"
              data-testid="lead-owner-message"
              data-tone={message.tone}
            >
              {message.text}
            </p>
          ) : null}
        </div>
      ) : null}
    </>
  );

  if (!renderChrome) return body;

  return (
    <section className="lead360-section lead360-owner" aria-label="Owner">
      <h4 className="lead360-section__title">
        <SectionIcon name="highlights" />
        {OWNER_ROW_LABEL}
      </h4>
      {body}
    </section>
  );
}

// --- Live container (wires state around the injected async fns) ------

export interface LeadOwnerControlsProps {
  leadId: number;
  ownership: LeadOwnershipView;
  /** See LeadOwnerControlsViewProps.showOwnerRow (default TRUE). */
  showOwnerRow?: boolean;
  /** See LeadOwnerControlsViewProps.renderChrome (default TRUE). */
  renderChrome?: boolean;
  me: DashboardMe | null;
  claim: (leadId: number) => Promise<unknown>;
  assign: (leadId: number, targetStaffId: number) => Promise<unknown>;
  release: (leadId: number) => Promise<unknown>;
  loadAssignableStaff: () => Promise<readonly AssignableStaffOption[]>;
  /** Re-fetch the workspace so the owner state updates after a mutation. */
  onChanged: () => void;
}

export default function LeadOwnerControls({
  leadId,
  ownership,
  showOwnerRow,
  renderChrome,
  me,
  claim,
  assign,
  release,
  loadAssignableStaff,
  onChanged,
}: LeadOwnerControlsProps): JSX.Element {
  const [staff, setStaff] = useState<readonly AssignableStaffOption[]>([]);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<OwnerControlMessage | null>(null);

  const fail = (kind: OwnerActionKind, err: unknown): void => {
    setSubmitting(false);
    setMessage({
      tone: 'error',
      text: ownerActionErrorMessage(kind, statusOf(err), codeOf(err)),
    });
  };

  const succeed = (text: string): void => {
    setSubmitting(false);
    setMessage({ tone: 'success', text });
    onChanged();
  };

  // Lazy-load the assignable-staff list once, when the caller may assign
  // (leadership). A fetch fault is non-fatal: an empty list renders the
  // "No staff to assign yet." copy rather than breaking the record.
  const mayAssign = canAssign(me);
  useEffect(() => {
    if (!mayAssign || staffLoaded || staffLoading) return;
    let cancelled = false;
    setStaffLoading(true);
    loadAssignableStaff()
      .then((rows) => {
        if (cancelled) return;
        setStaff([...rows]);
        setStaffLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setStaff([]);
        setStaffLoaded(true);
      })
      .finally(() => {
        if (!cancelled) setStaffLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mayAssign]);

  const onClaim = (): void => {
    setSubmitting(true);
    setMessage(null);
    claim(leadId)
      .then(() => succeed(CLAIM_SUCCESS_COPY))
      .catch((err: unknown) => fail('claim', err));
  };

  const onRelease = (): void => {
    setSubmitting(true);
    setMessage(null);
    release(leadId)
      .then(() => succeed(RELEASE_SUCCESS_COPY))
      .catch((err: unknown) => fail('release', err));
  };

  const onAssignSelect = (targetStaffId: number): void => {
    setSubmitting(true);
    setMessage(null);
    assign(leadId, targetStaffId)
      .then(() => succeed(ASSIGN_SUCCESS_COPY))
      .catch((err: unknown) => fail('assign', err));
  };

  return (
    <LeadOwnerControlsView
      ownership={ownership}
      showOwnerRow={showOwnerRow}
      renderChrome={renderChrome}
      me={me}
      staff={staff}
      staffLoading={staffLoading}
      submitting={submitting}
      message={message}
      onClaim={onClaim}
      onRelease={onRelease}
      onAssignSelect={onAssignSelect}
    />
  );
}
